<script lang="ts">
  import { onMount } from "svelte"
  import { slide } from "svelte/transition"
  import { xhr, toast, wait } from "$app/utils"
  import Sortable from "sortablejs"
  import { ChevronRight, ChevronDown } from "lucide-svelte"
  import QuarterlyGoal from "./QuarterlyGoal.svelte"
  import NewGoal from "./NewGoal.svelte"
  import Modal from "$app/components/Modal.svelte"
  import GoalDetail from "./GoalDetail.svelte"
  import TaskDetail from "./TaskDetail.svelte"
  import { appStore } from "$app/store.svelte"
  import { filterSortFn } from "./methods.svelte"

  interface Props {
    department: DepartmentType
    period: string
  }

  let { department, period }: Props = $props()

  const dates = appStore.dates
  const channel_id = appStore.channel_id
  let saving = $state(false)
  let openNewGoalModal = $state(false)
  let newGoalComp: NewGoal = $state({} as NewGoal)

  let openGoalDetailModal = $state(false)
  let currentGoal: GoalType = $state()
  let openTaskDetailModal = $state(false)
  let currentTask: TaskType = $state()

  let moveDepartmentModal = $state(false)
  let sortableSection: HTMLElement = $state()
  let newDeptId: number = $state()

  let nGoal = {
    name: "",
    status: "on_track",
    assignee_id: department.id,
    assignee_type: "Department",
    type: "QuarterlyGoal",
    expected_at: dates.next_date_end
  } as GoalType

  let subGoals = $state([])

  function setDepartmentGoals() {
    subGoals = filterSortFn(
      appStore.goals,
      period,
      dates,
      "QuarterlyGoal"
    ).filter((g) => g.assignee_id === department.id)
  }

  async function moveToDepartment(): Promise<void> {
    try {
      saving = true

      const goal_ids = [
        ...appStore.goals.filter((g) => g.assignee_id === newDeptId),
        ...subGoals
      ].map((g) => g.id)

      await xhr.post(
        `/plans/${department.plan_id}/goals/sort_by_department?channel=${channel_id}&move=true`,
        {
          id: newDeptId,
          goals: goal_ids
        }
      )

      moveDepartmentModal = false
    } catch (e) {
      console.error(e)
    } finally {
      saving = false
    }
  }

  async function updateGoalsOrder(items: GoalType[]): Promise<void> {
    try {
      let newGoals: GoalType[] = []

      const resp = await xhr.post(
        `/plans/${department.plan_id}/goals/sort_by_department.json?channel=${channel_id}`,
        { id: department.id, goals: items.map((s) => s.id) }
      )

      newGoals = resp.data

      newGoals.forEach((goal) => {
        const idx = appStore.goals.findIndex((g) => goal.id === g.id)
        appStore.goals[idx] = { ...appStore.goals[idx], ...goal }
      })
      setDepartmentGoals()

      setSortable()
    } catch (e) {
      console.error(e)
      const tmpGoals = [...appStore.goals]
      appStore.goals = []
      await wait(50)
      appStore.goals = [...tmpGoals]
      toast("There was an error updating the sort order", "error")
    }
  }

  let sort: any = $state()
  /**
   * Creates sortable for goals
   */
  function setSortable(): void {
    if (!sortableSection) return

    sort = Sortable.create(sortableSection, {
      animation: 200,
      group: `QuarterlyGoal-${department.id}`,
      scrollSensitivity: 150,
      // sort: false,
      delay: 400,
      delayOnTouchOnly: true,
      direction: "vertical",
      ghostClass: "opacity-50",
      dragClass: "border-green-400",
      emptyInsertThreshold: 20,
      onEnd: async (e: EventSortType) => {
        sort.options.sort = false
        // Move from another container
        // new sort order
        if (e.from.id === e.to.id) {
          let newGoals: GoalType[] = []
          e.from
            .querySelectorAll(".quarterly-goal")
            .forEach((elem: Element) => {
              const el = elem as HTMLElement
              const id = parseInt(el?.dataset?.goal_id)
              const cGoal = appStore.goals.find((g) => id === g.id)
              newGoals.push(cGoal)
            })

          await updateGoalsOrder(newGoals)
          sort.options.sort = true
        }
      },
      onStart: (evt: EventSortType) => {
        appStore.draggedElement = {
          ...appStore.draggedElement,
          type: "AnnualGoal",
          id: parseInt(evt.item.dataset.id),
          goal_id: parseInt(evt.item.dataset.parent_id)
        }
      }
    })

    // setTimeout(() => {
    //   document.addEventListener('dragover', (e) => {
    //     e.preventDefault()
    //   })
    // }, 200)
  }

  let mounted = $state(false)

  function toggleDepartment(): void {
    const idx = appStore.departments.findIndex((d) => d.id === department.id)
    appStore.departments[idx]._selected = !appStore.departments[idx]._selected
  }

  function openGoalDetail(goal): void {
    currentGoal = goal
    openGoalDetailModal = true
  }

  function openTaskDetail(task): void {
    currentTask = task
    openTaskDetailModal = true
  }

  onMount(async () => {
    setDepartmentGoals()
    mounted = true

    setTimeout(() => setSortable(), 200)
  })

  $effect(() => {
    if (appStore.goals) {
      setDepartmentGoals()
    }

    if (department._selected) {
      setSortable()
    }
  })
</script>

<div
  class="pt-5 h-16 pb-2 flex justify-between items-center border-t bg-gray-50 border-gray-200 px-1 hover:bg-gray-100 relative"
>
  {#if subGoals.length > 0 && period === "next"}
    <!--
    <button
      class="rounded text-xs px-2 absolute bottom-1 right-1 bg-blue-500 hover:bg-blue-600 text-white"
      data-name="open-move-goals-modal"
      onclick={() => (moveDepartmentModal = true)}
    >
      Re-assign
      <span class="font-bold">
        {subGoals.length}
      </span>
      goal{subGoals.length === 1 ? "" : "s"} to Department...
    </button>
    -->
  {/if}

  <!-- svelte-ignore a11y_click_events_have_key_events -->
  <!-- svelte-ignore a11y_no_static_element_interactions -->
  <div
    class={[
      "flex items-center pr-1 hover:cursor-pointer",
      department._selected ? "open" : ""
    ]}
    onclick={(e) => {
      e.stopPropagation()
      toggleDepartment()
    }}
    data-expanded={department._selected}
    data-name="department-{department.id}-expander"
  >
    {#if department._selected}
      <ChevronDown class="text-gray-500 w-5 h-5 mr-1" />
    {:else}
      <ChevronRight class="text-gray-500 w-5 h-5 mr-1" />
    {/if}

    <span class="text-xl text-gray-600 hover:cursor-pointer flex-grow">
      {department.name}
    </span>
  </div>
  {#if period === "next"}
    <button
      class="absolute top-1 right-1 bg-gray-200 border text-xs border-gray-300 hover:shadow-lg rounded text-gray-800 hover:bg-gray-500 hover:text-white px-3 py-0.5"
      type="button"
      onclick={() => {
        openNewGoalModal = true
      }}
    >
      + New Goal
    </button>
  {/if}
</div>

{#if department._selected}
  <section
    class="pl-2 quarterly-goal-{department.id}"
    bind:this={sortableSection}
    data-name="department-goals"
    transition:slide={{ duration: 300 }}
  >
    {#key subGoals}
      {#each subGoals as goal, idx (idx)}
        <QuarterlyGoal
          bind:goal={subGoals[idx]}
          {period}
          pos={idx + 1}
          allowGoalSelect={period === "next"}
          allowTaskSelect={period === "prev"}
          onopenGoalModal={(goal) => openGoalDetail(goal)}
          onopenTaskModal={(task) => openTaskDetail(task)}
        />
      {:else}
        <div class="w-full border border-gray-100 py-2 px-6">
          No Goals Found
        </div>
      {/each}
    {/key}
  </section>
{/if}

<Modal bind:open={moveDepartmentModal} class="w-full md:w-[600px]">
  <div>
    <label for="goal-department">
      Re-Assign goals from
      <span class="font-semibold">{department.name}</span> to:
    </label>
    <select bind:value={newDeptId} data-name="department" id="goal-department">
      {#each appStore.departments as dept (dept.id)}
        {#if dept.id !== department.id}
          <option value={dept.id}>{dept.name}</option>
        {/if}
      {/each}
    </select>
    <div class="py-2 text-center">
      <button
        class="btn btn-rounded success btn-big"
        disabled={saving}
        onclick={moveToDepartment}
        data-name="move-goals-button"
      >
        Move
      </button>
    </div>
  </div>
</Modal>

<Modal
  bind:open={openNewGoalModal}
  closeConfirm={true}
  onclose={() => {
    if (newGoalComp?.changesCount() > 1) {
      if (
        confirm(
          "Are you sure you want to close the modal? You will lose all your changes!"
        )
      ) {
        openNewGoalModal = false
      }
    } else {
      openNewGoalModal = false
    }
  }}
>
  <NewGoal
    goal={nGoal}
    bind:this={newGoalComp}
    loadParent={true}
    oncreate={(g) => {
      openNewGoalModal = false
      setSortable()
      //appStore.goals = [...appStore.goals, g]
    }}
  />
</Modal>

<Modal bind:open={openGoalDetailModal} large={true}>
  <GoalDetail goalId={currentGoal.id} />
</Modal>

<Modal bind:open={openTaskDetailModal} large={true}>
  <TaskDetail taskId={currentTask.id} onupdated={setDepartmentGoals} />
</Modal>
